import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ProjectsService } from '../../../../../../../core/projects.service';
import { CommonService } from '../../../../../../../../../shared/services/common.service';
import { MaterialService } from '../../../../../../../../../shared/services/material.service';
import { Material } from '../../../../../../../../../shared/models/material';
import { Project } from '../../../../../../../../../shared/models';

@Component({
    selector: 'app-delivery-tracking-materials-edit',
    templateUrl: './delivery-tracking-materials-edit.component.html',
    styleUrls: ['./delivery-tracking-materials-edit.component.scss'],
    standalone: false
})
export class DeliveryTrackingMaterialsEditComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() data: any;

  form: UntypedFormGroup;
  isFormDirty = false;
  errorMessages: any = {};
  errorMessageMap: Record<string, Record<string, string>> = {
    name: { required: 'This field is required.' },
    uom: { required: 'This field is required.' },
  };

  private readonly onDestroy = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private projectsService: ProjectsService,
    private materialService: MaterialService,
    private commonService: CommonService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      name: [this.data?.material?.name || '', [Validators.required]],
      uom: [this.data?.material?.uom || '', [Validators.required]],
      track_received: [this.data?.material?.track_received ?? false],
      track_used: [this.data?.material?.track_used ?? false],
    });

    this.projectsService.isAnyFormDirty.pipe(takeUntil(this.onDestroy)).subscribe(dirty => {
      this.isFormDirty = dirty;
    });

    this.form.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.isFormDirty = true;
      this.projectsService.isAnyFormDirty.next(true);
    });
  }

  ngAfterViewInit(): void {
    this.projectsService.isAnyFormDirty.next(false);
  }

  get isEditMode(): boolean {
    return !!this.data?.material?.id;
  }

  checkForErrors(currentField?: string): void {
    const validationErrors = this.commonService.checkFormValidation(
      this.form,
      this.errorMessageMap,
      currentField
    );
    if (validationErrors) {
      this.errorMessages = { ...this.errorMessages, ...validationErrors };
    }
  }

  submit(): void {
    if (this.form.invalid) {
      this.checkForErrors();
      return;
    }

    const raw = this.form.getRawValue();

    if (this.isEditMode) {
      const material = this.data.material as Material;
      material.name = raw.name;
      material.uom = raw.uom;
      material.track_received = raw.track_received;
      material.track_used = raw.track_used;

      this.materialService.update(material)
        .then(() => {
          this.snackBar.open('Material updated', '', { duration: 3000 });
          this.projectsService.isAnyFormDirty.next(false);
          this.projectsService.closeRightPanel.next();
          if (this.data?.onSave) { this.data.onSave(); }
        })
        .catch(() => {
          this.snackBar.open('Failed to update material', '', { duration: 3000 });
        });
    } else {
      const material = new Material({});
      material.name = raw.name;
      material.uom = raw.uom;
      material.track_received = raw.track_received;
      material.track_used = raw.track_used;

      const project = new Project({});
      project.id = this.data.projectId;

      this.materialService.create(material, project)
        .then(() => {
          this.snackBar.open('Material created', '', { duration: 3000 });
          this.projectsService.isAnyFormDirty.next(false);
          this.projectsService.closeRightPanel.next();
          if (this.data?.onSave) { this.data.onSave(); }
        })
        .catch(() => {
          this.snackBar.open('Failed to create material', '', { duration: 3000 });
        });
    }
  }

  cancel(): void {
    this.projectsService.isAnyFormDirty.next(false);
    this.projectsService.closeRightPanel.next();
  }

  ngOnDestroy(): void {
    this.projectsService.isAnyFormDirty.next(false);
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
